import React from "react";
import PropTypes from "prop-types";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "../buildStyles";
import Text from "../display/Text";
import ModalBase from "./ModalBase";
import FlexContainer from "../layout/FlexContainer";
import ButtonText from "../input/button/ButtonText";
import TextButtonBase from "../input/button/base/TextButtonBase";
import ScrollableContainer from "../layout/ScrollableContainer";

const useStyles = (theme, isKeyboardOpen) =>
  buildStyles({
    modalContent: {
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      backgroundColor: theme.colors.white,
      width: "100%",
      maxHeight: "70vh",
      padding: theme.spacing(4, 0),
      paddingBottom: `calc(env(safe-area-inset-bottom) + ${theme.spacing(isKeyboardOpen ? 0 : 3)})`,
      borderRadius: theme.spacing(2.5, 2.5, 0, 0),
    },
    header: {
      padding: theme.spacing(0, 4, 4),
      width: "100%",
      position: "relative",
      borderBottom: `1px solid ${theme.colors.border}`,
    },
  });

/**
 * BottomSheetModal is a component that displays a sliding panel from the bottom of the screen.
 * It includes a header with title and close button, and a scrollable content area. Or you can pass a custom content.
 *
 * @param {Object} props - The props for the BottomSheetModal component.
 * @param {boolean} props.isOpen - Whether the modal is open or not.
 * @param {Function} props.onClose - The function to call when the modal is closed.
 * @param {string} [props.title=""] - The title to display in the modal header.
 * @param {React.ReactNode} [props.customContent] - Custom content that replaces the default structure.
 * @param {React.ReactNode} props.children - The content to render inside the scrollable area.
 * @param {Object} [props.sx={container: {}, content: {}}] - Custom styles for the container and content.
 *
 * @returns {JSX.Element} The rendered BottomSheetModal component.
 */
const BottomSheetModal = ({
  isOpen,
  onClose,
  title = "",
  customContent,
  children,
  sx = {container: {}, content: {}},
}) => {
  const {theme, isKeyboardOpen} = useTheme();
  const classes = useStyles(theme, isKeyboardOpen);

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      className={classes.modalContent}
      sx={{
        height: "fit-content",
        overflowY: "hidden",
      }}
      wrapperProps={{
        sx: {
          zIndex: theme.zIndex.drawer,
          height: "100%",
          alignItems: "flex-end",
        },
      }}
    >
      <FlexContainer
        flexDirection="column"
        sx={{overflow: "hidden", ...sx.container}}
      >
        {customContent ? (
          customContent
        ) : (
          <>
            <FlexContainer
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              className={classes.header}
            >
              <ButtonText
                label="Close"
                onClick={onClose}
                buttonBase={TextButtonBase}
                color="primary"
                weight="semibold"
                typographyVariant="textMD"
                sx={{
                  position: "absolute",
                  left: theme.spacing(4),
                  padding: 0,
                }}
              />
              <Text variant="textLG" weight="semibold">
                {title}
              </Text>
            </FlexContainer>
            <ScrollableContainer
              sx={{
                scrollbarWidth: "none",
                padding: theme.spacing(3, 4, 0),
                ...sx.content,
              }}
            >
              {children}
            </ScrollableContainer>
          </>
        )}
      </FlexContainer>
    </ModalBase>
  );
};

BottomSheetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  customContent: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.shape({
    container: PropTypes.object,
    content: PropTypes.object,
  }),
};

export default BottomSheetModal;
